import {
  type CameraControls,
  type CameraControlsProps,
  CameraControls as DreiCameraControls,
} from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { type RefObject, useCallback, useEffect, useRef } from 'react';
import { type Object3D, PerspectiveCamera, Vector3 } from 'three';

import { usePlayerCameraControlsContext } from '../hooks/playerCameraControlsContext';
import { usePlayerFramingMetricsRef } from '../hooks/playerFramingMetricsContext';
import { attachPlayerCameraGizmoGate } from '../lib/attachPlayerCameraGizmoGate';
import { centerPlayerCameraOnTarget } from '../lib/centerPlayerCameraOnTarget';
import {
  isPlayerCameraUpdateBlocked,
  isPlayerCharacterFitBlocked,
} from '../lib/isPlayerCameraUpdateBlocked';
import {
  applyPlayerPerspectiveClippingForFraming,
  fitPlayerCameraToCharacter,
  getFitPlayerCameraPose,
  type FitPlayerCameraPose,
} from '../lib/fitPlayerCameraToCharacter';
import {
  type PlayerStore,
  usePlayerStore,
  usePlayerStoreApi,
} from '../store/playerStore';
import type { PlayerCameraControlsHandle } from '../types';
import PlayerFollowCamera from './PlayerFollowCamera';

/** Max frames to retry auto-fit while character bounds settle (e.g. first animated pose). */
const FIT_CHARACTER_SETTLE_MAX_FRAMES = 3;
/** Skip setLookAt when the computed framing pose is within this delta of the last apply. */
const FIT_CHARACTER_POSE_EPS = 0.015;
/** End settle early after this many consecutive frames with an unchanged pose. */
const FIT_CHARACTER_STABLE_FRAMES_TO_END = 2;
const DEFAULT_PLAYER_CAMERA_DOLLY_SPEED = 0.5;

type AppliedFitPose = {
  position: Vector3;
  target: Vector3;
  framingSphereRadius: number;
};

function isFitTargetMeshReady(store: PlayerStore): boolean {
  const actorId = store.state.activeActorId;
  if (!actorId) {
    return true;
  }

  const status = store.state.assetStatusByActorId[actorId];
  return status?.meshStatus === 'ready';
}

function hasFitPoseChanged(
  pose: FitPlayerCameraPose,
  last: AppliedFitPose | null,
  eps: number,
): boolean {
  if (!last) {
    return true;
  }

  return (
    pose.position.distanceTo(last.position) > eps ||
    pose.target.distanceTo(last.target) > eps ||
    Math.abs(pose.framingSphereRadius - last.framingSphereRadius) > eps
  );
}

const PLAYER_CAMERA_DEBUG_STORAGE_KEY = 'cartwheel:debug:playerCamera';
const PLAYER_CAMERA_DEBUG_POSITION_EPS = 1e-4;

type PlayerCameraDebugWindow = Window & {
  __CARTWHEEL_DEBUG_PLAYER_CAMERA__?: boolean;
};

function isPlayerCameraDebugEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const debugWindow = window as PlayerCameraDebugWindow;
  if (debugWindow.__CARTWHEEL_DEBUG_PLAYER_CAMERA__) {
    return true;
  }

  try {
    return localStorage.getItem(PLAYER_CAMERA_DEBUG_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function formatVector3(v: Vector3): string {
  return `(${v.x.toFixed(4)}, ${v.y.toFixed(4)}, ${v.z.toFixed(4)})`;
}

function logPlayerCameraDebug(
  source: string,
  details: Record<string, unknown>,
  controls?: PlayerCameraControlsHandle | null,
  positionScratch?: Vector3,
  targetScratch?: Vector3,
) {
  if (!isPlayerCameraDebugEnabled()) {
    return;
  }

  const payload: Record<string, unknown> = {
    source,
    at: performance.now(),
    ...details,
  };

  if (controls && positionScratch && targetScratch) {
    controls.getPosition(positionScratch);
    controls.getTarget(targetScratch);
    payload.controlsPosition = formatVector3(positionScratch);
    payload.controlsTarget = formatVector3(targetScratch);
    payload.controlsDistance = positionScratch.distanceTo(targetScratch);
  }

  // Use console.log — DevTools often hides console.debug unless Verbose is on.
  console.log('[PlayerCameraControls]', payload);
}

const playerCameraDebugAttached = new WeakSet<CameraControls>();

function attachPlayerCameraControlsDebug(
  instance: CameraControls,
  positionScratch: Vector3,
  targetScratch: Vector3,
): () => void {
  if (!isPlayerCameraDebugEnabled() || playerCameraDebugAttached.has(instance)) {
    return () => {};
  }

  playerCameraDebugAttached.add(instance);

  const handle = instance as PlayerCameraControlsHandle;
  const originalSetLookAt = handle.setLookAt.bind(handle);
  handle.setLookAt = (
    positionX: number,
    positionY: number,
    positionZ: number,
    targetX: number,
    targetY: number,
    targetZ: number,
    enableTransition?: boolean,
  ) => {
    const logAfter = logPlayerCameraSetLookAt(
      'setLookAt',
      handle,
      new Vector3(positionX, positionY, positionZ),
      new Vector3(targetX, targetY, targetZ),
      enableTransition ?? false,
      positionScratch,
      targetScratch,
    );
    originalSetLookAt(
      positionX,
      positionY,
      positionZ,
      targetX,
      targetY,
      targetZ,
      enableTransition,
    );
    logAfter?.();
  };

  let lastControlEventLog = 0;
  const logControlEvent = (type: string) => {
    const now = performance.now();
    if (type === 'update' && now - lastControlEventLog < 100) {
      return;
    }
    lastControlEventLog = now;
    logPlayerCameraDebug(`controls:${type}`, { type }, handle, positionScratch, targetScratch);
  };

  const onUpdate = () => logControlEvent('update');
  const onControlStart = () => logControlEvent('controlstart');
  const onControlEnd = () => logControlEvent('controlend');
  const onControl = () => logControlEvent('control');
  const onTransitionStart = () => logControlEvent('transitionstart');
  const onWake = () => logControlEvent('wake');

  instance.addEventListener('update', onUpdate);
  instance.addEventListener('controlstart', onControlStart);
  instance.addEventListener('controlend', onControlEnd);
  instance.addEventListener('control', onControl);
  instance.addEventListener('transitionstart', onTransitionStart);
  instance.addEventListener('wake', onWake);

  return () => {
    instance.removeEventListener('update', onUpdate);
    instance.removeEventListener('controlstart', onControlStart);
    instance.removeEventListener('controlend', onControlEnd);
    instance.removeEventListener('control', onControl);
    instance.removeEventListener('transitionstart', onTransitionStart);
    instance.removeEventListener('wake', onWake);
    playerCameraDebugAttached.delete(instance);
  };
}

function logPlayerCameraSetLookAt(
  source: string,
  controls: PlayerCameraControlsHandle,
  position: Vector3,
  target: Vector3,
  enableTransition: boolean,
  positionScratch: Vector3,
  targetScratch: Vector3,
  extra?: Record<string, unknown>,
) {
  if (!isPlayerCameraDebugEnabled()) {
    return;
  }

  controls.getPosition(positionScratch);
  controls.getTarget(targetScratch);
  const beforePosition = positionScratch.clone();
  const beforeTarget = targetScratch.clone();
  const beforeDistance = beforePosition.distanceTo(beforeTarget);

  return () => {
    controls.getPosition(positionScratch);
    controls.getTarget(targetScratch);
    const afterDistance = positionScratch.distanceTo(targetScratch);
    const positionDelta = positionScratch.distanceTo(beforePosition);
    const targetDelta = targetScratch.distanceTo(beforeTarget);
    const distanceDelta = afterDistance - beforeDistance;

    logPlayerCameraDebug(
      source,
      {
        action: 'setLookAt',
        enableTransition,
        requestedPosition: formatVector3(position),
        requestedTarget: formatVector3(target),
        beforePosition: formatVector3(beforePosition),
        beforeTarget: formatVector3(beforeTarget),
        afterPosition: formatVector3(positionScratch),
        afterTarget: formatVector3(targetScratch),
        positionDelta,
        targetDelta,
        distanceDelta,
        moved:
          positionDelta > PLAYER_CAMERA_DEBUG_POSITION_EPS ||
          targetDelta > PLAYER_CAMERA_DEBUG_POSITION_EPS,
        ...extra,
      },
      controls,
      positionScratch,
      targetScratch,
    );

    if (
      positionDelta > PLAYER_CAMERA_DEBUG_POSITION_EPS ||
      targetDelta > PLAYER_CAMERA_DEBUG_POSITION_EPS
    ) {
      console.trace(
        '[PlayerCameraControls] camera pose changed',
        source,
      );
    }
  };
}

function logPlayerCameraClipping(
  source: string,
  camera: PerspectiveCamera,
  cameraPosition: Vector3,
  target: Vector3,
  framingSphereRadius: number,
) {
  if (!isPlayerCameraDebugEnabled()) {
    return;
  }

  const beforeNear = camera.near;
  const beforeFar = camera.far;

  return () => {
    if (camera.near === beforeNear && camera.far === beforeFar) {
      return;
    }

    logPlayerCameraDebug(source, {
      action: 'applyPerspectiveClipping',
      framingSphereRadius,
      cameraPosition: formatVector3(cameraPosition),
      target: formatVector3(target),
      beforeNear,
      beforeFar,
      afterNear: camera.near,
      afterFar: camera.far,
    });
  };
}

export interface PlayerCameraControlsProps extends CameraControlsProps {
  cameraControlsRef?: RefObject<PlayerCameraControlsHandle | null>;
  /**
   * When true, frames the current character once it is available.
   * Pass the loaded character via `fitCharacterTarget` and/or `fitCharacterTargetRef`.
   * When omitted, the active actor scene is used. Manual camera input cancels
   * any pending fit until `fitCharacterKey` changes or the controls remount.
   */
  fitCharacter?: boolean;
  fitCharacterTarget?: Object3D | null;
  fitCharacterTargetRef?: RefObject<Object3D | null>;
  /**
   * Fraction of mesh height added above and below the bounds before fitting (default 0.1 = 10%).
   */
  fitCharacterVerticalPadding?: number;
  /** Multiplies the computed framing distance (values > 1 frame wider). */
  fitCharacterDistanceMultiplier?: number;
  /** When true (default), reframes the active player when playback stops (same as `frame()`). */
  relockTargetOnPlaybackStop?: boolean;
  /**
   * When this value changes, auto-fit runs again (same scene uuid may be reused after BVH/settings reload).
   */
  fitCharacterKey?: string | number;
}

function resolveFrameTarget({
  store,
  fitCharacterTarget,
  fitCharacterTargetRef,
}: {
  store: PlayerStore;
  fitCharacterTarget?: Object3D | null;
  fitCharacterTargetRef?: RefObject<Object3D | null>;
}) {
  const explicitTarget =
    fitCharacterTarget ?? fitCharacterTargetRef?.current ?? null;
  if (explicitTarget) {
    return explicitTarget;
  }

  const activeActorId = store.state.activeActorId;
  return activeActorId ? store.getActorScene(activeActorId) : null;
}

function captureInitialFrameDirection(
  controls: PlayerCameraControlsHandle,
  directionRef: RefObject<Vector3 | null>,
  positionScratch: Vector3,
  targetScratch: Vector3,
) {
  if (directionRef.current) {
    return;
  }

  const initialOffset = controls
    .getPosition(positionScratch)
    .sub(controls.getTarget(targetScratch));

  if (
    !Number.isFinite(initialOffset.lengthSq()) ||
    initialOffset.lengthSq() === 0
  ) {
    return;
  }

  directionRef.current = initialOffset.normalize().clone();
}

function PlayerCameraCharacterFit({
  enabled,
  fitCharacterTarget,
  fitCharacterTargetRef,
  verticalPadding,
  distanceMultiplier,
  fitCharacterKey,
  controlsRef,
  initialFrameDirectionRef,
  autoFitCancelledRef,
}: {
  enabled: boolean;
  fitCharacterTarget?: Object3D | null;
  fitCharacterTargetRef?: RefObject<Object3D | null>;
  verticalPadding: number;
  distanceMultiplier: number;
  fitCharacterKey?: string | number;
  controlsRef: RefObject<PlayerCameraControlsHandle | null>;
  initialFrameDirectionRef: RefObject<Vector3 | null>;
  autoFitCancelledRef: RefObject<boolean>;
}) {
  const camera = useThree((s) => s.camera);
  const store = usePlayerStoreApi();
  const framingMetricsRef = usePlayerFramingMetricsRef();
  const lastFramingKeyRef = useRef<string | null>(null);
  const settleRef = useRef(0);
  const stableFitFramesRef = useRef(0);
  const lastAppliedFitPoseRef = useRef<AppliedFitPose | null>(null);
  const framingSphereRadiusRef = useRef(1);
  const syncTargetScratch = useRef(new Vector3());
  const initialPositionScratch = useRef(new Vector3());
  const initialTargetScratch = useRef(new Vector3());

  useFrame(() => {
    const controls = controlsRef.current;
    if (!enabled) {
      lastFramingKeyRef.current = null;
      settleRef.current = 0;
      stableFitFramesRef.current = 0;
      lastAppliedFitPoseRef.current = null;
      return;
    }
    if (isPlayerCharacterFitBlocked(store)) {
      // Preserve fit state across gizmo drags so mouseUp does not look like a new framing key.
      return;
    }
    if (!controls || !(camera instanceof PerspectiveCamera)) {
      return;
    }

    captureInitialFrameDirection(
      controls,
      initialFrameDirectionRef,
      initialPositionScratch.current,
      initialTargetScratch.current,
    );

    const object = resolveFrameTarget({
      store,
      fitCharacterTarget,
      fitCharacterTargetRef,
    });
    if (!object) {
      // Keep lastFramingKeyRef so a brief mesh reload does not trigger reframing.
      return;
    }

    const revision =
      fitCharacterKey !== undefined && fitCharacterKey !== ''
        ? String(fitCharacterKey)
        : '';
    const framingKey = revision || object.uuid;

    if (lastFramingKeyRef.current !== framingKey) {
      lastFramingKeyRef.current = framingKey;
      settleRef.current = FIT_CHARACTER_SETTLE_MAX_FRAMES;
      stableFitFramesRef.current = 0;
      lastAppliedFitPoseRef.current = null;
      logPlayerCameraDebug('characterFit:framingKeyChanged', {
        framingKey,
        settleFrames: FIT_CHARACTER_SETTLE_MAX_FRAMES,
        objectUuid: object.uuid,
      });
    }

    if (autoFitCancelledRef.current) {
      settleRef.current = 0;
    }

    if (settleRef.current > 0) {
      if (!isFitTargetMeshReady(store)) {
        return;
      }

      const pose = getFitPlayerCameraPose(camera, object, {
        verticalPadding,
        viewDirection: initialFrameDirectionRef.current ?? undefined,
        distanceMultiplier,
      });
      if (pose) {
        framingSphereRadiusRef.current = pose.framingSphereRadius;
        const dist = pose.position.distanceTo(pose.target);
        if (framingMetricsRef) {
          framingMetricsRef.current.distance = dist;
          framingMetricsRef.current.sphereRadius = pose.framingSphereRadius;
        }

        const poseChanged = hasFitPoseChanged(
          pose,
          lastAppliedFitPoseRef.current,
          FIT_CHARACTER_POSE_EPS,
        );

        if (poseChanged) {
          stableFitFramesRef.current = 0;
          logPlayerCameraDebug('characterFit:settle', {
            settleRemaining: settleRef.current,
            framingKey,
            poseChanged: true,
          });
          controls.setLookAt(
            pose.position.x,
            pose.position.y,
            pose.position.z,
            pose.target.x,
            pose.target.y,
            pose.target.z,
            false,
          );
          const logClipping = logPlayerCameraClipping(
            'characterFit:settle',
            camera,
            pose.position,
            pose.target,
            pose.framingSphereRadius,
          );
          applyPlayerPerspectiveClippingForFraming(
            camera,
            pose.position,
            pose.target,
            pose.framingSphereRadius,
          );
          logClipping?.();
          lastAppliedFitPoseRef.current = {
            position: pose.position.clone(),
            target: pose.target.clone(),
            framingSphereRadius: pose.framingSphereRadius,
          };
        } else {
          stableFitFramesRef.current += 1;
          logPlayerCameraDebug('characterFit:settle', {
            settleRemaining: settleRef.current,
            framingKey,
            poseChanged: false,
            stableFrames: stableFitFramesRef.current,
          });
          if (
            stableFitFramesRef.current >= FIT_CHARACTER_STABLE_FRAMES_TO_END
          ) {
            settleRef.current = 0;
            return;
          }
        }
      }
      settleRef.current -= 1;
    } else {
      const orbitTarget = controls.getTarget(syncTargetScratch.current);
      const logClipping = logPlayerCameraClipping(
        'characterFit:orbitSync',
        camera,
        camera.position,
        orbitTarget,
        framingSphereRadiusRef.current,
      );
      applyPlayerPerspectiveClippingForFraming(
        camera,
        camera.position,
        orbitTarget,
        framingSphereRadiusRef.current,
      );
      logClipping?.();
      if (framingMetricsRef) {
        const dist = camera.position.distanceTo(orbitTarget);
        framingMetricsRef.current.distance = dist;
        framingMetricsRef.current.sphereRadius = framingSphereRadiusRef.current;
      }
    }
  });

  return null;
}

function PlayerCameraDebugSampler({
  controlsRef,
  store,
}: {
  controlsRef: RefObject<PlayerCameraControlsHandle | null>;
  store: PlayerStore;
}) {
  const camera = useThree((s) => s.camera);
  const positionScratch = useRef(new Vector3());
  const targetScratch = useRef(new Vector3());
  const lastControlsPosition = useRef(new Vector3());
  const lastControlsTarget = useRef(new Vector3());
  const lastCameraPosition = useRef(new Vector3());
  const lastLogTimeRef = useRef(0);
  const initializedRef = useRef(false);

  useFrame(() => {
    if (!isPlayerCameraDebugEnabled()) {
      initializedRef.current = false;
      return;
    }

    const controls = controlsRef.current;
    if (!controls) {
      return;
    }

    controls.getPosition(positionScratch.current);
    controls.getTarget(targetScratch.current);

    if (!initializedRef.current) {
      initializedRef.current = true;
      lastControlsPosition.current.copy(positionScratch.current);
      lastControlsTarget.current.copy(targetScratch.current);
      lastCameraPosition.current.copy(camera.position);
      return;
    }

    const posDelta = positionScratch.current.distanceTo(lastControlsPosition.current);
    const tgtDelta = targetScratch.current.distanceTo(lastControlsTarget.current);
    const camDelta = camera.position.distanceTo(lastCameraPosition.current);
    if (
      posDelta <= PLAYER_CAMERA_DEBUG_POSITION_EPS &&
      tgtDelta <= PLAYER_CAMERA_DEBUG_POSITION_EPS &&
      camDelta <= PLAYER_CAMERA_DEBUG_POSITION_EPS
    ) {
      return;
    }

    const now = performance.now();
    if (now - lastLogTimeRef.current < 100) {
      return;
    }
    lastLogTimeRef.current = now;
    lastControlsPosition.current.copy(positionScratch.current);
    lastControlsTarget.current.copy(targetScratch.current);
    lastCameraPosition.current.copy(camera.position);

    const controlsEnabled =
      'enabled' in controls
        ? (controls as PlayerCameraControlsHandle & { enabled?: boolean }).enabled
        : undefined;

    console.log('[PlayerCameraControls] pose sampler', {
      controlsPosition: formatVector3(positionScratch.current),
      controlsTarget: formatVector3(targetScratch.current),
      cameraPosition: formatVector3(camera.position),
      posDelta,
      tgtDelta,
      camDelta,
      controlsEnabled,
      gizmoDragging: isPlayerCameraUpdateBlocked(store),
      controlsVsCamera: positionScratch.current.distanceTo(camera.position),
    });
  });

  return null;
}

function PlayerCameraRelockOnPlaybackStop({
  enabled,
  controlsRef,
}: {
  enabled: boolean;
  controlsRef: RefObject<PlayerCameraControlsHandle | null>;
}) {
  const store = usePlayerStore();
  const isPlaying = store.state.transport.isPlaying;
  const wasPlayingRef = useRef(isPlaying);

  useEffect(() => {
    const wasPlaying = wasPlayingRef.current;
    wasPlayingRef.current = isPlaying;

    if (!enabled || !wasPlaying || isPlaying) {
      return;
    }

    if (isPlayerCharacterFitBlocked(store)) {
      return;
    }

    const controls = controlsRef.current;
    if (!controls) {
      return;
    }

    logPlayerCameraDebug('relockOnPlaybackStop:start', {
      wasPlaying,
      isPlaying,
    });

    const framed = controls.frame?.(true);
    logPlayerCameraDebug('relockOnPlaybackStop:frame', { framed });
    if (!framed) {
      logPlayerCameraDebug('relockOnPlaybackStop:center', {
        activeActorId: store.state.activeActorId,
        followTarget: store.state.follow.target,
      });
      centerPlayerCameraOnTarget({
        cameraControls: controls,
        store,
        options: { enableTransition: true },
      });
    }
  }, [controlsRef, enabled, isPlaying, store]);

  return null;
}

export default function PlayerCameraControls({
  cameraControlsRef,
  fitCharacter = false,
  fitCharacterTarget,
  fitCharacterTargetRef,
  fitCharacterVerticalPadding = 0.1,
  fitCharacterDistanceMultiplier = 1,
  relockTargetOnPlaybackStop = true,
  fitCharacterKey,
  dollySpeed = DEFAULT_PLAYER_CAMERA_DOLLY_SPEED,
  ...props
}: PlayerCameraControlsProps) {
  const store = usePlayerStoreApi();
  const camera = useThree((s) => s.camera);
  const { setCameraControls } = usePlayerCameraControlsContext();
  const localControlsRef = useRef<PlayerCameraControlsHandle | null>(null);
  const initialFrameDirectionRef = useRef<Vector3 | null>(null);
  const frameCameraRef = useRef<(enableTransition?: boolean) => boolean>(
    () => false,
  );
  const initialPositionScratch = useRef(new Vector3());
  const initialTargetScratch = useRef(new Vector3());
  const debugCleanupRef = useRef<(() => void) | null>(null);
  const interactionCleanupRef = useRef<(() => void) | null>(null);
  const autoFitCancelledRef = useRef(false);

  useEffect(() => {
    autoFitCancelledRef.current = false;
  }, [fitCharacterKey]);

  useEffect(() => {
    if (!isPlayerCameraDebugEnabled()) {
      return;
    }

    console.log(
      '[PlayerCameraControls] debug ON — filter console by "[PlayerCameraControls]". Disable: localStorage.removeItem("%s") then reload.',
      PLAYER_CAMERA_DEBUG_STORAGE_KEY,
    );
  }, []);

  frameCameraRef.current = (enableTransition = true) => {
    const controls = localControlsRef.current;
    const object = resolveFrameTarget({
      store,
      fitCharacterTarget,
      fitCharacterTargetRef,
    });

    if (
      !controls ||
      !(camera instanceof PerspectiveCamera) ||
      !object ||
      isPlayerCameraUpdateBlocked(store) ||
      isPlayerCharacterFitBlocked(store)
    ) {
      return false;
    }

    captureInitialFrameDirection(
      controls,
      initialFrameDirectionRef,
      initialPositionScratch.current,
      initialTargetScratch.current,
    );

    controls.getPosition(initialPositionScratch.current);
    controls.getTarget(initialTargetScratch.current);
    const beforePosition = initialPositionScratch.current.clone();
    const beforeTarget = initialTargetScratch.current.clone();

    logPlayerCameraDebug('frame:fitPlayerCameraToCharacter:start', {
      enableTransition,
      objectUuid: object.uuid,
      beforePosition: formatVector3(beforePosition),
      beforeTarget: formatVector3(beforeTarget),
    });

    const fitted = fitPlayerCameraToCharacter(controls, camera, object, {
      verticalPadding: fitCharacterVerticalPadding,
      viewDirection: initialFrameDirectionRef.current ?? undefined,
      distanceMultiplier: fitCharacterDistanceMultiplier,
      enableTransition,
    });

    controls.getPosition(initialPositionScratch.current);
    controls.getTarget(initialTargetScratch.current);
    const positionDelta = initialPositionScratch.current.distanceTo(beforePosition);
    const targetDelta = initialTargetScratch.current.distanceTo(beforeTarget);
    logPlayerCameraDebug('frame:fitPlayerCameraToCharacter:end', {
      fitted,
      afterPosition: formatVector3(initialPositionScratch.current),
      afterTarget: formatVector3(initialTargetScratch.current),
      positionDelta,
      targetDelta,
      moved:
        positionDelta > PLAYER_CAMERA_DEBUG_POSITION_EPS ||
        targetDelta > PLAYER_CAMERA_DEBUG_POSITION_EPS,
    });
    if (
      isPlayerCameraDebugEnabled() &&
      (positionDelta > PLAYER_CAMERA_DEBUG_POSITION_EPS ||
        targetDelta > PLAYER_CAMERA_DEBUG_POSITION_EPS)
    ) {
      console.trace('[PlayerCameraControls] camera pose changed', 'frame:fitPlayerCameraToCharacter');
    }

    if (fitted) {
      // An explicit frame command takes precedence over any pending auto-fit.
      autoFitCancelledRef.current = true;
    }
    return fitted;
  };

  const setRefs = useCallback(
    (instance: CameraControls | null) => {
      debugCleanupRef.current?.();
      debugCleanupRef.current = null;
      interactionCleanupRef.current?.();
      interactionCleanupRef.current = null;

      const handle = instance as PlayerCameraControlsHandle | null;
      if (handle) {
        handle.cancelAutoFit = () => {
          autoFitCancelledRef.current = true;
        };
        handle.frame = (enableTransition = true) => {
          if (isPlayerCameraUpdateBlocked(store)) {
            return false;
          }
          return frameCameraRef.current(enableTransition);
        };
      }
      localControlsRef.current = handle;
      setCameraControls(handle);

      if (instance && handle?.cancelAutoFit) {
        const cancelAutoFit = handle.cancelAutoFit;
        // Wheel input emits `control` without `controlstart`.
        instance.addEventListener('controlstart', cancelAutoFit);
        instance.addEventListener('control', cancelAutoFit);
        interactionCleanupRef.current = () => {
          instance.removeEventListener('controlstart', cancelAutoFit);
          instance.removeEventListener('control', cancelAutoFit);
        };
        attachPlayerCameraGizmoGate(instance, store);
        debugCleanupRef.current = attachPlayerCameraControlsDebug(
          instance,
          initialPositionScratch.current,
          initialTargetScratch.current,
        );
      }

      if (cameraControlsRef) {
        (
          cameraControlsRef as { current: PlayerCameraControlsHandle | null }
        ).current = handle;
      }
    },
    [cameraControlsRef, setCameraControls, store],
  );

  useEffect(
    () => () => {
      debugCleanupRef.current?.();
      interactionCleanupRef.current?.();
    },
    [],
  );

  return (
    <>
      <DreiCameraControls ref={setRefs} dollySpeed={dollySpeed} {...props} />
      <PlayerCameraDebugSampler controlsRef={localControlsRef} store={store} />
      <PlayerFollowCamera cameraControlsRef={cameraControlsRef} />
      <PlayerCameraRelockOnPlaybackStop
        enabled={relockTargetOnPlaybackStop}
        controlsRef={localControlsRef}
      />
      {fitCharacter && (
        <PlayerCameraCharacterFit
          enabled={fitCharacter}
          fitCharacterTarget={fitCharacterTarget}
          fitCharacterTargetRef={fitCharacterTargetRef}
          verticalPadding={fitCharacterVerticalPadding}
          distanceMultiplier={fitCharacterDistanceMultiplier}
          fitCharacterKey={fitCharacterKey}
          controlsRef={localControlsRef}
          initialFrameDirectionRef={initialFrameDirectionRef}
          autoFitCancelledRef={autoFitCancelledRef}
        />
      )}
    </>
  );
}
